/**
 * 下次注意事項 API hooks(REQ-20260918002)。
 *
 * 客戶與廠商各一張表、各一組路徑,但前端要用的地方(分頁、應收單、進貨單)
 * 行為完全相同,所以 hook 一律吃 `ownerType` 決定打哪條路徑 ——
 * 慣例沿用 `features/interviews/api.ts` 的 `collectionPath()`。
 *
 * query key 一律帶 `ownerType` + `ownerId`,客戶 3 號與廠商 3 號不會互相汙染快取。
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApiSuccess,
  CreateReminderInput,
  ReminderResponse,
  ReminderStatus,
  UpdateReminderInput,
} from '@app/shared';
import { apiClient } from '../../lib/api-client';

export type ReminderOwnerType = 'customer' | 'supplier';

function collectionPath(ownerType: ReminderOwnerType): string {
  return ownerType === 'customer' ? 'customers' : 'suppliers';
}

function listKey(
  ownerType: ReminderOwnerType,
  ownerId: number | undefined,
  status: ReminderStatus,
): unknown[] {
  return ['party-reminders', ownerType, ownerId, status];
}

async function fetchReminders(
  ownerType: ReminderOwnerType,
  ownerId: number,
  status: ReminderStatus,
): Promise<ReminderResponse[]> {
  const res = await apiClient.get<ApiSuccess<ReminderResponse[]>>(
    `/${collectionPath(ownerType)}/${ownerId}/reminders`,
    { params: { status } },
  );
  return res.data.data;
}

/** 分頁內的完整清單(未完成 + 已完成,由呼叫端自己分組)。 */
export function useReminders(
  ownerType: ReminderOwnerType,
  ownerId: number | undefined,
  status: ReminderStatus = 'all',
) {
  return useQuery({
    queryKey: listKey(ownerType, ownerId, status),
    enabled: ownerId != null,
    queryFn: () => fetchReminders(ownerType, ownerId!, status),
  });
}

/**
 * 未完成的清單。分頁徽章只要數量、單據上的提醒列要內容 ——
 * 兩者共用同一支查詢,不另外開一支「只回數量」的端點:
 * 未完成的量本來就少(多到要分頁就代表沒有人在結它),多撈幾個字省下一次往返。
 */
export function useOpenReminders(
  ownerType: ReminderOwnerType,
  ownerId: number | undefined,
) {
  return useQuery({
    queryKey: listKey(ownerType, ownerId, 'open'),
    enabled: ownerId != null,
    staleTime: 1000 * 30,
    queryFn: () => fetchReminders(ownerType, ownerId!, 'open'),
  });
}

/** 新增 / 編輯 / 完成 / 刪除之後,這家 owner 底下所有 status 的查詢一起失效。 */
function useInvalidate(ownerType: ReminderOwnerType, ownerId: number) {
  const qc = useQueryClient();
  return () =>
    void qc.invalidateQueries({
      queryKey: ['party-reminders', ownerType, ownerId],
      exact: false,
    });
}

export function useCreateReminder(ownerType: ReminderOwnerType, ownerId: number) {
  const invalidate = useInvalidate(ownerType, ownerId);
  return useMutation({
    mutationFn: async (input: CreateReminderInput): Promise<ReminderResponse> => {
      const res = await apiClient.post<ApiSuccess<ReminderResponse>>(
        `/${collectionPath(ownerType)}/${ownerId}/reminders`,
        input,
      );
      return res.data.data;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateReminder(ownerType: ReminderOwnerType, ownerId: number) {
  const invalidate = useInvalidate(ownerType, ownerId);
  return useMutation({
    mutationFn: async (args: {
      id: number;
      input: UpdateReminderInput;
    }): Promise<ReminderResponse> => {
      const res = await apiClient.patch<ApiSuccess<ReminderResponse>>(
        `/${collectionPath(ownerType)}/${ownerId}/reminders/${args.id}`,
        args.input,
      );
      return res.data.data;
    },
    onSuccess: invalidate,
  });
}

/** 「完成」/「復原」是同一支 PATCH 的兩個方向,獨立成 hook 讓呼叫端讀起來直白。 */
export function useToggleReminderDone(ownerType: ReminderOwnerType, ownerId: number) {
  const invalidate = useInvalidate(ownerType, ownerId);
  return useMutation({
    mutationFn: async (args: { id: number; done: boolean }): Promise<ReminderResponse> => {
      const res = await apiClient.patch<ApiSuccess<ReminderResponse>>(
        `/${collectionPath(ownerType)}/${ownerId}/reminders/${args.id}`,
        { done: args.done },
      );
      return res.data.data;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteReminder(ownerType: ReminderOwnerType, ownerId: number) {
  const invalidate = useInvalidate(ownerType, ownerId);
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(
        `/${collectionPath(ownerType)}/${ownerId}/reminders/${id}`,
      );
    },
    onSuccess: invalidate,
  });
}
